import { createReducer } from '@reduxjs/toolkit';

import * as editorActions from '../actions/editor.action';
import * as metadataActions from '../actions/metadata.action';
import * as pageActions from '../actions/page.action';

function getLastItemOfChainByPageId (chain, pageId) {
  const lastItem = [...chain].reverse().find(item => Number(item.page_id) === Number(pageId));
  if (!lastItem) return null;

  return { pageId: Number(lastItem.page_id), id: lastItem.element_id };
}

function getFirstIndexOfChainByPageId (chain, pageId) {
  return chain.findIndex(item => Number(item.page_id) === pageId);
}

function getLastIndexOfChainByPageId (chain, pageId) {
  let lastIndex = chain.length - 1;

  for (let i = 0; i < chain.length; i++) {
    if (Number(chain[i].page_id) === pageId) lastIndex = i;
  }

  return lastIndex;
}

const setMetadata = (state, { payload }) => {
  Object.entries(payload).forEach(([key, value]) => {
    state[key] = value;
  });
};

const addToOrderedListsChain = (state, { payload }) => {
  if (!state.bullet_and_ordered_list?.list_styles) return;

  // elements: [{ pageId: 102, id: 'svg_2' }]
  const { elements, chainId } = payload;
  const pageId = Number(elements[0].pageId);

  const list = state.bullet_and_ordered_list.list_styles[chainId];

  if (!list) return;
  if (!Array.isArray(list.chains)) list.chains = [];

  const elementsToAdd = elements.map(item => ({ page_id: Number(item.pageId), element_id: item.id }));
  const nextTo = payload.nextTo || getLastItemOfChainByPageId(list.chains, pageId);
  const newChain = [];
  let pushed = false;

  for (let i = 0; i < list.chains.length; i++) {
    const current = list.chains[i];
    // If no next to found, push elementsToAdd to the beginning of the chain
    // Pushed flag is to prevent pushing elementsToAdd more than once
    if (!nextTo && !pushed) {
      newChain.unshift(...elementsToAdd, current);
      pushed = true;
    } else if (Number(current.page_id) === nextTo?.pageId && current.element_id === nextTo?.id) {
      newChain.push(current, ...elementsToAdd);
    } else {
      const isInChain = newChain.find(item => Number(item.page_id) === Number(current.page_id) && item.element_id === current.element_id);
      if (!isInChain) {
        newChain.push(current);
      }
    }
  }

  if (!newChain.length) newChain.push(...elementsToAdd);

  if (newChain.length === list.chains.length && JSON.stringify(newChain) === JSON.stringify(list.chains)) return;

  list.chains = newChain;
};

const removeFromOrderedListsChain = (state, { payload }) => {
  if (!state.bullet_and_ordered_list?.list_styles) return;

  // elements = [{ pageId: 1, id: 1}, {pageId: 1, id: 2}]
  const { elements, chainId } = payload;
  const list = state.bullet_and_ordered_list.list_styles[chainId];
  if (!list || !Array.isArray(list.chains)) return;

  const newChain = list.chains.filter(
    (item) =>
      !elements.find(
        (element) =>
          Number(element.pageId) === Number(item.page_id) &&
          element.id === item.element_id
      )
  );

  if (newChain.length === list.chains.length) return;

  list.chains = newChain;
};

const removePageFromChain = (state, { payload }) => {
  const { id: pageId } = payload;
  if (!state.bullet_and_ordered_list?.list_styles) return;

  Object.values(state.bullet_and_ordered_list.list_styles).forEach(value => {
    const newChain = value.chains.filter(item => Number(item.page_id) !== pageId);
    if (newChain.length === value.chains.length) return;

    value.chains = newChain;
  });
};

const deletePage = (state, action) => {
  removePageFromChain(state, action);

  const deletePageId = Number(action.payload.id);

  if (state.animation?.pages?.[deletePageId]) {
    delete state.animation.pages[deletePageId];
  }

  if (state.media?.[deletePageId]) {
    delete state.media[deletePageId];
  }
};

const duplicatePageFromChain = (state, { payload }) => {
  const pageId = Number(payload.id);
  const basedOn = Number(payload.basedOn);

  if (state.bullet_and_ordered_list?.list_styles) {
    Object.values(state.bullet_and_ordered_list.list_styles).forEach(value => {
      const duplicatedChain = value.chains.filter(item => {
        return Number(item.page_id) === basedOn;
      });
      if (!duplicatedChain.length) return;

      const fistIndexOfPage = getFirstIndexOfChainByPageId(value.chains, basedOn);
      const lastIndexOfPage = getLastIndexOfChainByPageId(value.chains, basedOn);

      value.chains = [].concat(
        value.chains.slice(0, fistIndexOfPage),
        duplicatedChain,
        duplicatedChain.map(item => ({ ...item, page_id: pageId })),
        value.chains.slice(lastIndexOfPage + 1)
      );
    });
  }

  if (state.media?.[basedOn]) {
    state.media[pageId] = [...state.media[basedOn]];
  }
};

const updatePageOrderInChain = (state, { payload }) => {
  const newPageOrder = payload.map(Number);
  if (!state.bullet_and_ordered_list?.list_styles) return;

  Object.values(state.bullet_and_ordered_list.list_styles).forEach(value => {
    value.chains = value.chains.sort((a, b) => {
      const aIndex = newPageOrder.indexOf(Number(a.page_id));
      const bIndex = newPageOrder.indexOf(Number(b.page_id));

      if (aIndex < bIndex) return -1;
      if (aIndex > bIndex) return 1;

      return 0;
    });
  });
};

const onTextLinkAdded = (state, { payload }) => {
  const { prev, next } = payload;
  const orderedList = state?.bullet_and_ordered_list?.list_styles;
  const isValidLink = prev && next && prev.pageId === next.pageId;

  if (!isValidLink || !orderedList) return;

  Object.entries(orderedList).forEach(([key, list]) => {
    const { chains } = list;
    if (!chains || !Array.isArray(chains)) return;

    const previousItem = chains.find(item => Number(item.page_id) === Number(prev.pageId) && item.element_id === prev.id);
    const nextItem = chains.find(item => Number(item.page_id) === Number(next.pageId) && item.element_id === next.id);

    if (!previousItem || !nextItem) return;

    addToOrderedListsChain(state, {
      payload: {
        elements: [{ pageId: Number(next.pageId), id: next.id }],
        chainId: key,
        nextTo: {
          pageId: Number(previousItem.page_id),
          id: previousItem.element_id
        }
      }
    });
  });
};

const initialState = {};

export default createReducer(initialState, builder => {
  builder
    .addCase(metadataActions.setMetadata, setMetadata)
    .addCase(metadataActions.saveMetadata.fulfilled, setMetadata)
    .addCase(metadataActions.addToOrderedListChain, addToOrderedListsChain)
    .addCase(metadataActions.removeFromOrderedListChain, removeFromOrderedListsChain)
    .addCase(pageActions.deletePage, deletePage)
    .addCase(pageActions.duplicatePage, duplicatePageFromChain)
    .addCase(editorActions.setPagesOrder, updatePageOrderInChain)
    .addCase(editorActions.addTextLink, onTextLinkAdded)
  ;
});
